import { Modal } from "antd";
import Router from "next/router";
import UnprocessableEntity from "@components/unprocessable-entity";
import Cookies from "js-cookie";
import authService from "@http/services/auth.service";
import axios from "axios";
import { i18n } from "next-i18next";
import { getTenantConfig } from "src/utils/tenant";

/** True while a refresh is in progress; shared so concurrent 401s wait for the same refresh. */
let refreshPromise: Promise<boolean> | null = null;

function isRefreshRequest(config: { url?: string; baseURL?: string }): boolean {
  const url = config.url ?? "";
  return url.includes("keycloak/refresh");
}

function persistReturnPath(): void {
  if (typeof window === "undefined") {
    return;
  }

  const returnTo = `${window.location.pathname}${window.location.search}`;
  if (!returnTo || returnTo === "/" || returnTo.includes("keycloak")) {
    return;
  }

  try {
    localStorage.setItem("redirect_after_login", JSON.stringify(returnTo));
  } catch {
    // ignore quota / private mode
  }
}

export const httpClient = axios.create({
  baseURL: typeof window !== "undefined" ? getTenantConfig().apiUrl : "/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Add a request interceptor
httpClient.interceptors.request.use(function (config) {
  const token = Cookies.get("keycloak_access_token");

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  config.headers["Accept-Language"] = Router.locale;

  return config;
});

// Response interceptor
httpClient.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const requestConfig = error.config;
    const retriedAfterRefresh = (requestConfig as Record<string, unknown>).__retriedAfterRefresh === true;

    switch (status) {
      case 401: {
        // If this was the refresh request itself or we already retried after refresh, logout
        if (isRefreshRequest(requestConfig) || retriedAfterRefresh) {
          persistReturnPath();
          authService.logout();
          break;
        }

        // Try refresh once: wait for any in-flight refresh, or start one
        if (!refreshPromise) {
          refreshPromise = (async () => {
            try {
              // Keycloak routes (user, refresh, logout) are exposed at /api/keycloak/*.
              const config = getTenantConfig();
              const res = await axios.get(config.keycloakRefreshUrl, {
                withCredentials: true,
                headers: requestConfig.headers ? { ...requestConfig.headers } : {},
              });
              return res.status === 200;
            } catch {
              return false;
            } finally {
              refreshPromise = null;
            }
          })();
        }

        const refreshed = await refreshPromise;
        if (refreshed) {
          (requestConfig as Record<string, unknown>).__retriedAfterRefresh = true;
          return httpClient.request(requestConfig);
        }
        persistReturnPath();
        authService.logout();
        break;
      }

      case 403:
        if (requestConfig.headers?.hasOwnProperty("isLogin")) {
          authService.logout();
        } else {
          Router.replace("/403", "/403", { locale: Router.locale });
        }
        break;

      case 404:
        // Auth bootstrap can probe /api/keycloak/user and should not hard-redirect to 404.
        if (!requestConfig.headers?.hasOwnProperty("isAuthBootstrap")) {
          Router.push("/404", "/404", { locale: Router.locale });
        }
        break;

      case 405:
      case 500:
        Router.push("/500", "/500", { locale: Router.locale });
        break;

      case 422:
        Modal.info({
          title: i18n?.t("validation:422.title"),
          content: (
            <UnprocessableEntity error={data.error} errors={data.errors} />
          ),
        });
        break;

      default:
        break;
    }

    return Promise.reject(error);
  },
);

export default httpClient;
